import { useMemo } from 'react';
import { useTenant } from './useTenant';
import { DEFAULT_TAX_CONFIG, type TaxConfig } from '@/utils/tax';

export interface CompanyTaxResult extends TaxConfig {
  cardFeeRate: number;
}

/**
 * Live tax + card-fee config for the current tenant. Falls back to AU defaults
 * (10% GST inclusive, 1.5% card fee) until the company profile loads, so callers
 * never crash on first render. Memoized so callers can include the result in
 * useEffect dependency arrays without triggering re-runs every render.
 */
export function useCompanyTax(): CompanyTaxResult {
  const { company } = useTenant();

  return useMemo<CompanyTaxResult>(() => ({
    enabled:   company?.tax_enabled   ?? DEFAULT_TAX_CONFIG.enabled,
    rate:      company?.tax_rate      ?? DEFAULT_TAX_CONFIG.rate,
    label:     company?.tax_label     ?? DEFAULT_TAX_CONFIG.label,
    inclusive: company?.tax_inclusive ?? DEFAULT_TAX_CONFIG.inclusive,
    cardFeeRate: company?.card_processing_fee_rate ?? 1.5,
  }), [
    company?.tax_enabled,
    company?.tax_rate,
    company?.tax_label,
    company?.tax_inclusive,
    company?.card_processing_fee_rate,
  ]);
}
